import axios from 'axios';
import { atlasAuthApi } from '../../services/api/atlasAuthApi';
import { getSimulationApiBaseUrl } from '../../services/getAtlasApiBaseUrl';

// ─── Errors ───────────────────────────────────────────────────────────────────

export function readAxiosDetail(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { detail?: unknown } | undefined;
    if (data && typeof data.detail === 'string') return data.detail;
    if (error.message) return error.message;
  }
  if (error instanceof Error) return error.message;
  return 'Request failed';
}

// ─── Types ───────────────────────────────────────────────────────────────────

export interface CompanyInfo {
  customer_id: string;
  customer_name: string;
  user_count: number;
  license_count: number;
  license_total: number;
}

export interface CompanyUser {
  id: string;
  username: string;
  full_name: string;
  email: string;
  disabled: boolean;
  company_role: 'company_admin' | 'member';
  created_at: string;
}

export interface CompanyInvitation {
  invitation_id: string;
  customer_id: string;
  email: string;
  company_role: 'company_admin' | 'member';
  token: string;
  created_by: string;
  expires_at: string;
  accepted_at: string | null;
  created_at: string;
}

export interface CompanyLicense {
  license_id: string;
  user_id: string | null;
  user_email: string | null;
  expires_at: string;
  created_at: string;
  updated_at: string;
}

export interface InvitationDetails {
  email: string;
  company_role: 'company_admin' | 'member';
  customer_name: string;
  expires_at: string;
  is_expired: boolean;
}

export interface AcceptInvitationResponse {
  access_token: string;
  token_type: string;
}

// ─── Public invitation endpoints (no auth) ──────────────────────────────────

const apiBase = getSimulationApiBaseUrl();

export async function getInvitationDetails(token: string): Promise<InvitationDetails> {
  const res = await axios.get<InvitationDetails>(`${apiBase}/invitations/${token}`);
  return res.data;
}

export async function acceptInvitation(
  token: string,
  payload: { full_name: string; password: string }
): Promise<AcceptInvitationResponse> {
  const res = await axios.post<AcceptInvitationResponse>(
    `${apiBase}/invitations/${token}/accept`,
    payload
  );
  return res.data;
}

// ─── Company info ────────────────────────────────────────────────────────────

export async function getCompanyInfo(): Promise<CompanyInfo> {
  const res = await atlasAuthApi.get<CompanyInfo>('/company/me');
  return res.data;
}

// ─── Company users ───────────────────────────────────────────────────────────

export async function listCompanyUsers(): Promise<CompanyUser[]> {
  const res = await atlasAuthApi.get<CompanyUser[]>('/company/users');
  return Array.isArray(res.data) ? res.data : [];
}

export async function updateCompanyUser(
  userId: string,
  payload: Partial<{ company_role: 'company_admin' | 'member'; disabled: boolean }>
): Promise<CompanyUser> {
  const res = await atlasAuthApi.put<CompanyUser>(`/company/users/${userId}`, payload);
  return res.data;
}

// ─── Company invitations ─────────────────────────────────────────────────────

export async function listCompanyInvitations(): Promise<CompanyInvitation[]> {
  const res = await atlasAuthApi.get<CompanyInvitation[]>('/company/invitations');
  return Array.isArray(res.data) ? res.data : [];
}

export async function createCompanyInvitation(payload: {
  email: string;
  company_role: 'company_admin' | 'member';
  expires_in_days: number;
}): Promise<CompanyInvitation> {
  const res = await atlasAuthApi.post<CompanyInvitation>('/company/invitations', payload);
  return res.data;
}

export async function cancelCompanyInvitation(invitationId: string): Promise<void> {
  await atlasAuthApi.delete(`/company/invitations/${invitationId}`);
}

// ─── Company licenses ────────────────────────────────────────────────────────

export async function listCompanyLicenses(): Promise<CompanyLicense[]> {
  const res = await atlasAuthApi.get<CompanyLicense[]>('/company/licenses');
  return Array.isArray(res.data) ? res.data : [];
}

export async function assignCompanyLicense(payload: {
  user_id: string;
}): Promise<CompanyLicense> {
  const res = await atlasAuthApi.post<CompanyLicense>('/company/licenses', payload);
  return res.data;
}

export async function unassignCompanyLicense(licenseId: string): Promise<void> {
  await atlasAuthApi.delete(`/company/licenses/${licenseId}`);
}
